import React, { createContext, useContext, useState, useMemo } from 'react';
import type { FC, ReactNode } from 'react';

import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics';
import {
	ADD_PAGE_COMMENT_EXPERIENCE,
	ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
	ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { startEditorExperiences } from '@confluence/experience-tracker/entry-points/startExperienceForRoute';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

type ObjectCommentAddExperienceEntryPoint =
	/**
	 * Used when a user adds a new page comment by clicking open the footer comments section at bottom of the page.
	 */
	| 'pageFooter'
	/**
	 * Used when a user adds a new page comment by clicking open the footer comments section from
	 * ObjectAddCommentContainerLegacy. To be deprecated.
	 */
	| 'pageFooterLegacy'
	/**
	 * Used when a user adds a new page comment using a keyboard shortcut from Renderer mode.
	 */
	| 'keyboardShortcutView'
	/**
	 * Used when a user adds a new page comment using a keyboard shortcut from Editor (i.e. LiveDoc) mode.
	 */
	| 'keyboardShortcutEditor';

type ObjectCommentContextValue = {
	expanded: boolean;
	footerEditorEntry?: AddFooterCommentMode;
	width?: string;
	isObjectCommentWrapperVisible: boolean;
};

export enum AddFooterCommentMode {
	FOOTER = 'footer', // The footer comments section
	STICKY_DRAFT = 'sticky_draft', // The sticky draft that persists outside of the footer
	STICKY_FOOTER = 'sticky_footer', // The sticky footer that persists outside of the footer
}

type ObjectCommentDispatchContextValue = {
	expandCommentContainer: (props?: { entryPoint?: ObjectCommentAddExperienceEntryPoint }) => void;
	closeCommentContainer: () => void;
	setWrapperWidth: (width?: string | number) => void;
	setIsObjectCommentWrapperVisible: (visible: boolean) => void;
	setFooterEditorEntry: (footerEditorEntry?: AddFooterCommentMode) => void;
};

export const ObjectCommentDispatchContext = createContext<ObjectCommentDispatchContextValue>({
	expandCommentContainer: () => {},
	closeCommentContainer: () => {},
	setWrapperWidth: () => {},
	setIsObjectCommentWrapperVisible: () => {},
	setFooterEditorEntry: () => {},
});
ObjectCommentDispatchContext.displayName = 'ObjectCommentDispatchContext';

export const ObjectCommentContext = createContext<ObjectCommentContextValue>({
	expanded: false,
	footerEditorEntry: undefined,
	width: undefined,
	isObjectCommentWrapperVisible: false,
});
ObjectCommentContext.displayName = 'ObjectCommentContext';

type ObjectCommentProviderProps = {
	children: ReactNode;
};

export const ObjectCommentProvider: FC<ObjectCommentProviderProps> = ({ children }) => {
	const [expanded, setExpanded] = useState(false);
	const [footerEditorEntry, setFooterEditorEntry] = useState<AddFooterCommentMode | undefined>(
		undefined,
	);
	const [width, setWidth] = useState<string | undefined>(undefined);
	const [isObjectCommentWrapperVisible, setIsObjectCommentWrapperVisible] = useState(false);

	const { edition } = useSessionData();

	const providerValue = useMemo(
		() => ({
			expanded,
			footerEditorEntry,
			width,
			isObjectCommentWrapperVisible,
		}),
		[expanded, footerEditorEntry, width, isObjectCommentWrapperVisible],
	);

	const dispatchProviderValue = useMemo(
		() => ({
			expandCommentContainer: (props?: { entryPoint?: ObjectCommentAddExperienceEntryPoint }) => {
				const { entryPoint } = props ?? {};
				if (entryPoint && fg('confluence_fe_add_page_comment_exp_coverage')) {
					startEditorExperiences(
						{
							name: ADD_PAGE_COMMENT_EXPERIENCE,
							attributes: {
								editor: 'FABRIC',
								isSingleExperience: true,
								edition,
								source: AnalyticsSource.PAGE_FOOTER,
								entryPoint,
							},
						},
						{
							name: ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
						},
						{
							name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
						},
					);
				}
				setExpanded(true);
			},
			closeCommentContainer: () => {
				setFooterEditorEntry(undefined);
				setExpanded(false);
			},
			setWrapperWidth: (width?: string | number) => {
				setWidth(width ? `${width}px` : '100%');
			},
			setIsObjectCommentWrapperVisible: (visible: boolean) => {
				setIsObjectCommentWrapperVisible(visible);
			},
			setFooterEditorEntry: (footerEditorEntry?: AddFooterCommentMode) => {
				setFooterEditorEntry(footerEditorEntry);
			},
		}),
		[setExpanded, setFooterEditorEntry, setWidth, setIsObjectCommentWrapperVisible, edition],
	);

	return (
		<ObjectCommentContext.Provider value={providerValue}>
			<ObjectCommentDispatchContext.Provider value={dispatchProviderValue}>
				{children}
			</ObjectCommentDispatchContext.Provider>
		</ObjectCommentContext.Provider>
	);
};

export const useObjectCommentContext = () => {
	return useContext(ObjectCommentContext);
};

export const useObjectCommentDispatchContext = () => {
	return useContext(ObjectCommentDispatchContext);
};
